import React, { Suspense, lazy, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Provider, useSelector } from 'react-redux';
import { Router, Route, Switch } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import {
  GrowthBookProvider as InitialGrowthBookProvider,
  useFeature,
} from '@growthbook/growthbook-react';
import SingleLayout from './layouts/SingleLayout/SingleLayout';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { CommonConfigurationProvider } from './components/CommonConfiguration/CommonConfigurationProvider';
import SEOQueryProvider from './components/SEOWrapper/SEOQueryProvider';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import Maintenance from './components/Maintenance/Maintenance';
import Loader from './components/Loader/Loader';
import RouterContextProvider from './components/Router/RouterContextProvider';
import SeoTextLoadingProvider from './AppSCCOM/components/SEOWrapper/SeoTextLoadingProvider/SeoTextLoadingProvider';
import { useGrowthBook } from './services/growthbook';
import { FEATURES } from './constants/featureToggling';
import {
  ROOT,
  GAME,
  DEMO_GAME,
  SPECIAL_OFFERS,
  BONUSES,
  BONUS_PAGE,
  RULES,
  RULES_DETAIL,
  SEARCH,
  COMMON_ERROR,
  NO_MATCH,
  BONUS_HISTORY,
  BONUSES_COLLECTING_GAMES,
  BONUSES_WAGERING_GAMES,
  BONUSES_WAGERING_GAMES_SCCOM,
  SPECIAL_OFFER_PAGE,
  CATEGORY_DETAIL,
  CATEGORY_DETAIL_PAGINATED,
  PARAM_NAME,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
  CATEGORY_LIST,
  PROVIDER_LIST,
  PROVIDER_DETAIL,
  PROVIDER_DETAIL_PAGINATED,
} from './constants/routeTemplates';
import './assets/styles/common/index.css';
import '@modulor/react/dist/index.css';

import WageringGamesAsyncSCCOM from './AppSCCOM/pages/WageringGames/WageringGamesAsync';
import { Redirect } from 'react-router-dom';
import { queryClient } from './services/queryClient';
import {
  getCategoryDetailRoute,
  getProviderDetailsRoute,
  getServerErrorRoute,
} from './utils/routes';
import { RECENT_GAMES_CATEGORY } from './redux/constants/categories';
import { useIsSC } from './hooks/useIsSC';
import { useViewConfig } from './hooks/useViewConfig';
import { isLocalizationInitializedSelector } from './redux/reducers/app/initialization';
import {
  ERROR_PAGE_COLLECTION,
  MAIN_LIST,
  CATEGORY_DETAILS,
  RECENT_GAMES_LIST,
  SEARCH_PAGE,
  BASE_LAYOUT,
  COLLECTING_PAGE,
  SPECIAL_OFFERS_PAGE,
  SPECIAL_OFFER_PAGE_COLLECTION,
  GAME_PAGE,
} from './constants/viewConfig';
import { BONUSES_HISTORY_CATEGORY } from './AppSCCOM/hooks/useBonusCategories';
import { useRouteTransition } from './hooks/useRouteTransition';

// dynamic pages
const Categories = lazy(() => import('./pages/Categories/Categories'));
const Providers = lazy(() => import('./pages/Providers/Providers'));
const ProviderDetails = lazy(() =>
  import('./pages/ProviderDetails/ProviderDetails')
);
const Bonuses = lazy(() => import('./pages/Bonuses/Bonuses'));
const Bonus = lazy(() => import('./pages/Bonus/Bonus'));
const BonusHistory = lazy(() => import('./pages/BonusHistory/BonusHistory'));
const Rules = lazy(() => import('./pages/Rules/Rules'));
const RulesDetails = lazy(() => import('./pages/RulesDetails/RulesDetails'));
const HallOfFameList = React.lazy(() =>
  import('./pages/HallOfFame/HallOfFame')
);
const WageringGames = lazy(() => import('./pages/WageringGames/WageringGames'));

// SCCOM

const CurrentBonusesSC = lazy(() =>
  import('./AppSCCOM/pages/CurrentBonuses/CurrentBonuses')
);
const BonusesHistorySC = React.lazy(() =>
  import('./AppSCCOM/pages/BonusesHistory/BonusesHistory')
);
const BonusDetailsSC = React.lazy(() =>
  import('./AppSCCOM/pages/BonusDetails/BonusDetails')
);
const BonusLayoutSC = React.lazy(() =>
  import('./AppSCCOM/components/BonusLayout/BonusLayout')
);

export const GrowthBookProvider = ({ children }) => {
  const { data, isLoading } = useGrowthBook();
  const isLocalizationReady = useSelector(isLocalizationInitializedSelector);

  if (isLoading || !isLocalizationReady) {
    return null;
  }

  return (
    <InitialGrowthBookProvider growthbook={data}>
      {children}
    </InitialGrowthBookProvider>
  );
};

const CustomBonusesRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        <Route path={BONUSES} exact component={Bonuses} />
        <Route path={BONUS_HISTORY} exact component={BonusHistory} />
        <Route path={BONUS_PAGE} component={Bonus} />
      </Switch>
    </Suspense>
  );
};

const CustomBonusesRoutesSC = () => {
  const renderBonusesRoutes = useCallback(
    (category) => (
      <>
        {category.categoryId === BONUSES_HISTORY_CATEGORY ? (
          <PrivateRoute path="*">
            <BonusesHistorySC />
          </PrivateRoute>
        ) : (
          <CurrentBonusesSC currentCategory={category} />
        )}

        <Route exact path={BONUS_PAGE}>
          <Suspense fallback={''}>
            <BonusDetailsSC currentCategory={category} />
          </Suspense>
        </Route>
      </>
    ),
    []
  );

  return (
    <Suspense fallback={<Loader />}>
      <BonusLayoutSC>{renderBonusesRoutes}</BonusLayoutSC>
    </Suspense>
  );
};

const BonusesRoutes = () => {
  const isSC = useIsSC();

  return isSC ? <CustomBonusesRoutesSC /> : <CustomBonusesRoutes />;
};

const WageringGamesRoutes = () => {
  const isSC = useIsSC();

  switch (true) {
    case isSC:
      return (
        <Switch>
          <Route
            exact
            path={BONUSES_WAGERING_GAMES}
            component={() => <Redirect to={BONUSES_WAGERING_GAMES_SCCOM} />}
          />
          <Route
            exact
            path={BONUSES_WAGERING_GAMES_SCCOM}
            component={WageringGamesAsyncSCCOM}
          />
        </Switch>
      );

    default:
      return (
        <Switch>
          <Route
            exact
            path={BONUSES_WAGERING_GAMES}
            component={WageringGames}
          />
          <Route
            exact
            path={BONUSES_WAGERING_GAMES_SCCOM}
            component={() => <Redirect to={getServerErrorRoute(404)} />}
          />
        </Switch>
      );
  }
};

const AppRoutes = () => {
  useRouteTransition();
  const isHallOfFameEnabled = useFeature(FEATURES.ENABLE_HALL_OF_FAME).on;
  const areRulesEnabled = useFeature(FEATURES.HOW_TO_PLAY).on;

  const BaseLayout = useViewConfig(BASE_LAYOUT);
  const Main = useViewConfig(MAIN_LIST);
  const CollectingGames = useViewConfig(COLLECTING_PAGE);
  const CategoryDetails = useViewConfig(CATEGORY_DETAILS);
  const RecentGames = useViewConfig(RECENT_GAMES_LIST);
  const ErrorPage = useViewConfig(ERROR_PAGE_COLLECTION);
  const Search = useViewConfig(SEARCH_PAGE);
  const SpecialOffer = useViewConfig(SPECIAL_OFFER_PAGE_COLLECTION);
  const SpecialOffers = useViewConfig(SPECIAL_OFFERS_PAGE);
  const Game = useViewConfig(GAME_PAGE);

  return (
    <Switch>
      {/* Internal Server Error*/}
      <Route
        path={COMMON_ERROR}
        render={(props) => (
          <SingleLayout>
            <ErrorPage {...props} />
          </SingleLayout>
        )}
      />
      <Route
        path={GAME}
        render={(props) => (
          <SingleLayout>
            <Game {...props} />
          </SingleLayout>
        )}
      />
      <Route
        exact
        path={DEMO_GAME}
        render={(props) => (
          <Redirect
            to={GAME.replace(`:${PARAM_NAME.GAME}`, props.match.params.gameId)}
          />
        )}
      />

      <Route
        path={[
          ROOT,
          CATEGORY_LIST,
          CATEGORY_DETAIL_PAGINATED,
          CATEGORY_DETAIL,
          PROVIDER_LIST,
          PROVIDER_DETAIL,
          PROVIDER_DETAIL_PAGINATED,
          SPECIAL_OFFERS,
          SPECIAL_OFFER_PAGE,
          BONUSES,
          BONUS_HISTORY,
          BONUS_PAGE,
          RULES,
          RULES_DETAIL,
          SEARCH,
          BONUSES_COLLECTING_GAMES,
          BONUSES_WAGERING_GAMES,
          BONUSES_WAGERING_GAMES_SCCOM,
          HALL_OF_FAME,
          HALL_OF_FAME_HISTORY,
        ]}
        exact
        component={(props) => (
          <BaseLayout withSidebar {...props}>
            <Suspense fallback={<Loader />}>
              <Switch>
                <PrivateRoute exact path={BONUSES_COLLECTING_GAMES}>
                  <CollectingGames />
                </PrivateRoute>
                <Route
                  exact
                  path={[BONUSES_WAGERING_GAMES, BONUSES_WAGERING_GAMES_SCCOM]}
                  component={WageringGamesRoutes}
                />
                {/* common */}

                <Route
                  exact
                  path={[HALL_OF_FAME, HALL_OF_FAME_HISTORY]}
                  component={(...props) =>
                    isHallOfFameEnabled ? (
                      <HallOfFameList {...props} />
                    ) : (
                      <Redirect to={ROOT} />
                    )
                  }
                />

                <Route path={SPECIAL_OFFERS} exact component={SpecialOffers} />
                <Route
                  path={SPECIAL_OFFER_PAGE}
                  exact
                  component={SpecialOffer}
                />
                <Route
                  path={[BONUSES, BONUS_HISTORY, BONUS_PAGE]}
                  exact
                  component={BonusesRoutes}
                />

                <Route
                  path={RULES_DETAIL}
                  component={(...props) =>
                    areRulesEnabled ? (
                      <RulesDetails {...props} />
                    ) : (
                      <Redirect to={ROOT} />
                    )
                  }
                />
                <Route
                  path={RULES}
                  component={(...props) =>
                    areRulesEnabled ? (
                      <Rules {...props} />
                    ) : (
                      <Redirect to={ROOT} />
                    )
                  }
                />
                <Route path={SEARCH} component={Search} />
                <Route path={CATEGORY_LIST} exact component={Categories} />
                <Route path={PROVIDER_LIST} exact component={Providers} />
                <Route
                  path={CATEGORY_DETAIL.replace(
                    `:${PARAM_NAME.CATEGORY}`,
                    RECENT_GAMES_CATEGORY
                  )}
                  exact
                  component={RecentGames}
                />
                <Route path={[CATEGORY_DETAIL, CATEGORY_DETAIL_PAGINATED]}>
                  <Switch>
                    <Route
                      path={CATEGORY_DETAIL}
                      exact
                      component={CategoryDetails}
                    />

                    <Route
                      path={CATEGORY_DETAIL_PAGINATED}
                      render={(props) => {
                        const page = props.match.params[PARAM_NAME.PAGE];
                        const categoryId =
                          props.match.params[PARAM_NAME.CATEGORY];

                        // eslint-disable-next-line
                        return page && page != 1 ? (
                          <CategoryDetails {...props} />
                        ) : (
                          <Redirect to={getCategoryDetailRoute(categoryId)} />
                        );
                      }}
                    />
                  </Switch>
                </Route>

                <Route path={[PROVIDER_DETAIL, PROVIDER_DETAIL_PAGINATED]}>
                  <Switch>
                    <Route
                      path={PROVIDER_DETAIL}
                      exact
                      component={ProviderDetails}
                    />

                    <Route
                      path={PROVIDER_DETAIL_PAGINATED}
                      render={(props) => {
                        const page = props.match.params[PARAM_NAME.PAGE];
                        const providerId =
                          props.match.params[PARAM_NAME.PROVIDER];

                        // eslint-disable-next-line
                        return page && page != 1 ? (
                          <ProviderDetails {...props} />
                        ) : (
                          <Redirect to={getProviderDetailsRoute(providerId)} />
                        );
                      }}
                    />
                  </Switch>
                </Route>
                {/* DEFAULT */}
                <Route path={ROOT} exact component={Main} />
              </Switch>
            </Suspense>
          </BaseLayout>
        )}
      />
      {/* Not Found */}
      {/* Edge case for error state handler (Redirect) */}
      <Route
        path={NO_MATCH}
        render={(props) => (
          <SingleLayout>
            <ErrorPage status={404} {...props} />
          </SingleLayout>
        )}
      ></Route>
    </Switch>
  );
};

const Routes = () => {
  useEffect(() => {
    return () => {
      queryClient.clear();
    };
  }, []);

  const isMaintenanceMode = useFeature(FEATURES.SHOW_MAINTENANCE_PAGE).on;

  return isMaintenanceMode ? (
    <Maintenance />
  ) : (
    <QueryClientProvider client={queryClient}>
      <AppRoutes />
    </QueryClientProvider>
  );
};

const App = ({ store, history }) => (
  <Provider store={store}>
    <PersistGate persistor={persistStore(store)}>
      <GrowthBookProvider>
        <CommonConfigurationProvider>
          <SEOQueryProvider>
            <Router history={history}>
              <ErrorBoundary>
                <SeoTextLoadingProvider>
                  <RouterContextProvider>
                    <Routes />
                  </RouterContextProvider>
                </SeoTextLoadingProvider>
              </ErrorBoundary>
            </Router>
          </SEOQueryProvider>
        </CommonConfigurationProvider>
      </GrowthBookProvider>
    </PersistGate>
  </Provider>
);

App.propTypes = {
  store: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.object.isRequired,
};

export default App;
